// Searchable superadmin pickers: choose an org or user from clearly identifiable options —
// never type or paste ids.

import { useEffect, useState } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'

import {
    useLookupOrgsQuery,
    useLookupUsersQuery,
    type OrgOption,
    type UserOption,
} from '@/apis/adminApi'

import {
    matchesOrg,
    matchesUser,
    orgPrimary,
    orgSecondary,
    userPrimary,
    userSecondary,
} from './pickerOptions'

function useDebounced(value: string, ms = 250): string {
    const [v, setV] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms)
        return () => clearTimeout(t)
    }, [value, ms])
    return v
}

interface PickerProps<T> {
    value: T | null
    onChange: (value: T | null) => void
    label?: string
    helperText?: string
    size?: 'small' | 'medium'
    required?: boolean
}

export function OrgPicker({
    value,
    onChange,
    label = 'Organization',
    helperText,
    size = 'small',
    required,
}: PickerProps<OrgOption>) {
    const [input, setInput] = useState('')
    const q = useDebounced(input)
    const { data = [], isFetching } = useLookupOrgsQuery(q)
    return (
        <Autocomplete<OrgOption>
            value={value}
            onChange={(_e, v) => onChange(v)}
            inputValue={input}
            onInputChange={(_e, v) => setInput(v)}
            options={data}
            filterOptions={opts => opts.filter(o => matchesOrg(o, input) || matchesOrg(o, q))}
            getOptionLabel={o => `${orgPrimary(o)} — ${o.owners[0] ?? o.shortId}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={isFetching}
            noOptionsText={q ? 'No matching organizations' : 'Type a name, email, domain or id'}
            renderOption={(props, o) => {
                const { key, ...rest } = props as typeof props & { key: string }
                return (
                    <Box component="li" key={key} {...rest} sx={{ display: 'block !important' }}>
                        <Typography variant="body2" fontWeight={600}>
                            {orgPrimary(o)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {orgSecondary(o)}
                        </Typography>
                    </Box>
                )
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    required={required}
                    placeholder="Search name, owner email, project, domain or id"
                    helperText={value ? orgSecondary(value) : helperText}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isFetching ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    )
}

export function UserPicker({
    value,
    onChange,
    label = 'User',
    helperText,
    size = 'small',
    required,
}: PickerProps<UserOption>) {
    const [input, setInput] = useState('')
    const q = useDebounced(input)
    const { data = [], isFetching } = useLookupUsersQuery(q)
    return (
        <Autocomplete<UserOption>
            value={value}
            onChange={(_e, v) => onChange(v)}
            inputValue={input}
            onInputChange={(_e, v) => setInput(v)}
            options={data}
            filterOptions={opts => opts.filter(u => matchesUser(u, input) || matchesUser(u, q))}
            getOptionLabel={u => userPrimary(u)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={isFetching}
            noOptionsText={q ? 'No matching users' : 'Type a name, email or org'}
            renderOption={(props, u) => {
                const { key, ...rest } = props as typeof props & { key: string }
                return (
                    <Box component="li" key={key} {...rest} sx={{ display: 'block !important' }}>
                        <Typography variant="body2" fontWeight={600}>
                            {userPrimary(u)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {userSecondary(u)}
                        </Typography>
                    </Box>
                )
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    size={size}
                    required={required}
                    placeholder="Search name, email or org"
                    helperText={value ? userSecondary(value) : helperText}
                />
            )}
        />
    )
}
